import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { getEmailDomain, getOrganization } from "../services/authService";
import { doc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import {
  setupBilling,
  getBillingInfo,
  getInvoices,
} from "../services/billingService";
import "./Billing.css";

const LICENSE_TIERS = {
  up_to_2000: {
    name: "Up to 2,000 students",
    price: 1000,
  },
  "2001_to_5000": {
    name: "2,001-5,000 students",
    price: 2000,
  },
  "5001_to_10000": {
    name: "5,001-10,000 students",
    price: 3000,
  },
  "10001_to_20000": {
    name: "10,001-20,000 students",
    price: 4500,
  },
  "20000_plus": {
    name: "20,000+ students",
    price: null, // Custom pricing
  },
};

// On-campus org pricing (per semester)
const ON_CAMPUS_TIERS = {
  "1_to_25": {
    name: "1-25 members",
    pricePerSemester: 75,
    pricePerYear: 150,
  },
  "26_to_50": {
    name: "26-50 members",
    pricePerSemester: 125,
    pricePerYear: 250,
  },
  "51_to_100": {
    name: "51-100 members",
    pricePerSemester: 225,
    pricePerYear: 450,
  },
  "101_to_200": {
    name: "101-200 members",
    pricePerSemester: 350,
    pricePerYear: 700,
  },
  "200_plus": {
    name: "200+ members",
    pricePerSemester: null, // Custom pricing
    pricePerYear: null,
  },
};

const Billing = () => {
  const { currentUser, orgDomain, isOrgAdminUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [billingInfo, setBillingInfo] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [orgType, setOrgType] = useState(null);
  const [memberCount, setMemberCount] = useState(null);
  const [memberCountVerified, setMemberCountVerified] = useState(false);
  const [fundedByUniversity, setFundedByUniversity] = useState(false);
  const [billingExempt, setBillingExempt] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [setupStep, setSetupStep] = useState(1); // 1: member count (on-campus) or tier (university), 2: billing info, 3: term commitment, 4: terms
  const [setupData, setSetupData] = useState({
    licenseTier: "",
    memberCount: "",
    billingContactEmail: "",
    poNumber: "",
    termSemester: "",
    termYear: new Date().getFullYear(),
    billingInterval: "semester", // university only: 'semester' | 'yearly'. yearly = 2x semester
    termsAccepted: false,
  });
  const [processing, setProcessing] = useState(false);

  const loadBillingData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const domain = orgDomain || getEmailDomain(currentUser?.email);
      if (!domain) {
        setError("Unable to determine organization domain.");
        return;
      }

      // Get organization to check type
      const org = await getOrganization(domain);
      if (org) {
        setOrgType(org.orgType || 'university');
        setMemberCount(org.memberCount || null);
        setMemberCountVerified(org.memberCountVerified || false);
        if (org.fundedByUniversity) {
          setFundedByUniversity(true);
          setLoading(false);
          return; // No billing UI for university-funded orgs
        }
        if (org.billingExempt) {
          setBillingExempt(true);
          setLoading(false);
          return; // Internal development organizations do not require billing
        }
      }

      const [billing, invoiceData] = await Promise.all([
        getBillingInfo(domain),
        getInvoices(domain),
      ]);

      setBillingInfo(billing);
      setInvoices(invoiceData.invoices || []);

      // Show setup if billing not configured
      // For on-campus orgs, also check if member count is verified
      if (!billing.stripeCustomerId) {
        if (org?.orgType === 'on_campus_org' && !org?.memberCountVerified) {
          // On-campus org needs member count verification first
          setShowSetup(true);
          setSetupStep(1);
        } else if (org?.orgType === 'on_campus_org' && org?.memberCountVerified && org?.memberCount) {
          // Member count is verified - automatically determine tier and skip to billing info
          const autoTier = getTierFromMemberCount(org.memberCount);
          setSetupData(prev => ({
            ...prev,
            licenseTier: autoTier,
            memberCount: org.memberCount,
          }));
          setMemberCount(org.memberCount);
          setMemberCountVerified(true);
          setShowSetup(true);
          setSetupStep(2); // Skip tier selection, go to billing info
        } else {
          setShowSetup(true);
        }
      }
    } catch (err) {
      console.error("Error loading billing data:", err);
      setError(err.message || "Failed to load billing information.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgDomain, isOrgAdminUser]);

  useEffect(() => {
    if (orgDomain && isOrgAdminUser) {
      loadBillingData();
    } else if (!isOrgAdminUser) {
      setError("You must be an admin to access billing.");
      setLoading(false);
    }
  }, [orgDomain, isOrgAdminUser, loadBillingData]);

  const getTierFromMemberCount = (count) => {
    if (count <= 25) return "1_to_25";
    if (count <= 50) return "26_to_50";
    if (count <= 100) return "51_to_100";
    if (count <= 200) return "101_to_200";
    return "200_plus";
  };

  const handleMemberCountSubmit = async (e) => {
    e.preventDefault();
    const count = parseInt(setupData.memberCount);
    if (!count || count < 1) {
      setError("Please enter a valid member count.");
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      const domain = orgDomain || getEmailDomain(currentUser?.email);
      // Save member count to organization (pending verification)
      const orgRef = doc(db, 'organizations', domain);
      await setDoc(orgRef, {
        memberCount: count,
        memberCountVerified: false,
        memberCountRequestedAt: new Date(),
      }, { merge: true });

      setMemberCount(count);
      setMemberCountVerified(false);
      setError('');
      alert('Member count submitted. A SyllySync administrator will verify this before you can proceed with billing setup. Please refresh the page after verification.');
      // Reload to check verification status
      await loadBillingData();
    } catch (err) {
      console.error('Error submitting member count:', err);
      setError('Failed to submit member count. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleTierSelect = (tier) => {
    if (tier === "20000_plus") {
      alert(
        "For 20,000+ students, please contact us for custom pricing. We will set up your billing manually."
      );
      return;
    }
    setSetupData({ ...setupData, licenseTier: tier });
    // Stay on step 1 so user can choose billing interval (semester vs yearly), then click Continue
  };

  const handleOnCampusTierSelect = (tier) => {
    if (tier === "200_plus") {
      alert(
        "For 200+ members, please contact us for custom pricing. We will set up your billing manually."
      );
      return;
    }
    setSetupData({ ...setupData, licenseTier: tier });
    setSetupStep(2);
  };

  const handleBillingInfoSubmit = (e) => {
    e.preventDefault();
    if (!setupData.billingContactEmail) {
      setError("Billing contact email is required.");
      return;
    }
    // For on-campus orgs, make sure we have a license tier (should be auto-set if verified)
    if (orgType === 'on_campus_org' && !setupData.licenseTier) {
      // Try to auto-determine if we have member count
      if (memberCount) {
        const autoTier = getTierFromMemberCount(memberCount);
        setSetupData(prev => ({ ...prev, licenseTier: autoTier }));
        setSetupStep(3);
        return;
      }
      setError("Pricing tier could not be determined. Please contact support.");
      return;
    }
    setSetupStep(3);
  };

  const handleTermCommitmentSubmit = (e) => {
    e.preventDefault();
    if (!setupData.termSemester || !setupData.termYear) {
      setError("Please select both semester and year.");
      return;
    }
    setSetupStep(4); // Move to terms step
  };

  const handleTermsAccept = async () => {
    if (!setupData.termsAccepted) {
      setError("You must accept the Terms of Service to continue.");
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      const domain = orgDomain || getEmailDomain(currentUser?.email);
      await setupBilling({
        domain,
        ...setupData,
        orgType: orgType,
        memberCount: memberCount,
      });

      // Reload billing data
      await loadBillingData();
      setShowSetup(false);
      setSetupStep(1);
      setSetupData({
        licenseTier: "",
        memberCount: "",
        billingContactEmail: "",
        poNumber: "",
        termSemester: "",
        termYear: new Date().getFullYear(),
        billingInterval: "semester",
        termsAccepted: false,
      });
    } catch (err) {
      console.error("Error setting up billing:", err);
      setError(err.message || "Failed to setup billing. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatCurrency = (amount, currency = "usd") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  if (loading) {
    return (
      <div className="billing-page">
        <div className="page-header">
          <h1>Billing</h1>
        </div>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading billing information...</p>
        </div>
      </div>
    );
  }

  if (!isOrgAdminUser) {
    return (
      <div className="billing-page">
        <div className="page-header">
          <h1>Billing</h1>
        </div>
        <div className="error-state">
          <p>You must be an admin to access billing.</p>
        </div>
      </div>
    );
  }

  if (fundedByUniversity) {
    return (
      <div className="billing-page">
        <div className="page-header">
          <h1>Billing</h1>
          {orgDomain && <p className="org-domain">Domain: {orgDomain}</p>}
        </div>
        <div className="info-state" style={{ padding: '24px', background: '#f0fdfa', borderRadius: '8px', color: '#0d9488' }}>
          <p><strong>This organization is funded by your university.</strong> No billing or payment is required.</p>
        </div>
      </div>
    );
  }

  if (billingExempt) {
    return (
      <div className="billing-page">
        <div className="page-header">
          <h1>Billing</h1>
          {orgDomain && <p className="org-domain">Domain: {orgDomain}</p>}
        </div>
        <div className="info-state" style={{ padding: '24px', background: '#f0fdfa', borderRadius: '8px', color: '#0d9488' }}>
          <p><strong>This internal development organization has full access.</strong> No billing or payment is required.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="billing-page">
      <div className="page-header">
        <h1>Billing</h1>
        {orgDomain && <p className="org-domain">Domain: {orgDomain}</p>}
        {!showSetup && billingInfo?.stripeCustomerId && (
          <button onClick={() => loadBillingData()} className="refresh-button">
            🔄 Refresh
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {showSetup && !billingInfo?.stripeCustomerId ? (
        <div className="billing-setup">
          <h2>Setup Billing</h2>

          {setupStep === 1 && orgType === 'on_campus_org' && (
            <div className="setup-step">
              {!memberCountVerified ? (
                <>
                  <h3>Enter Member Count</h3>
                  <p className="step-description">
                    Please enter the number of members in your on-campus organization. 
                    This will be verified by a SyllySync administrator before you can proceed with billing setup.
                  </p>
                  <form onSubmit={handleMemberCountSubmit}>
                    <div className="form-group">
                      <label>
                        Number of Members <span className="required">*</span>
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={setupData.memberCount}
                        onChange={(e) =>
                          setSetupData({
                            ...setupData,
                            memberCount: e.target.value,
                          })
                        }
                        required
                        placeholder="Enter number of members"
                        disabled={processing}
                      />
                      {memberCount && !memberCountVerified && (
                        <div className="info-message" style={{ marginTop: '12px', padding: '12px', background: '#f0fdfa', borderRadius: '8px', color: '#229aa5' }}>
                          <strong>Member count submitted:</strong> {memberCount} members. 
                          Waiting for SyllySync administrator verification.
                        </div>
                      )}
                    </div>
                    <div className="form-actions">
                      <button
                        type="submit"
                        className="btn-primary"
                        disabled={processing || !setupData.memberCount || memberCountVerified}
                      >
                        {processing ? "Submitting..." : memberCountVerified ? "Verified ✓" : "Submit for Verification"}
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <>
                  <h3>Pricing Tier</h3>
                  <p className="step-description">
                    Your member count ({memberCount} members) has been verified. Based on your member count, your pricing tier has been automatically determined.
                  </p>
                  <div className="tier-display" style={{ 
                    padding: '24px', 
                    background: '#f0fdfa', 
                    borderRadius: '12px', 
                    border: '2px solid #2ab3c0',
                    marginBottom: '24px'
                  }}>
                    {setupData.licenseTier && ON_CAMPUS_TIERS[setupData.licenseTier] ? (
                      <>
                        <div style={{ marginBottom: '16px' }}>
                          <h4 style={{ margin: '0 0 8px 0', color: '#2ab3c0' }}>
                            {ON_CAMPUS_TIERS[setupData.licenseTier].name}
                          </h4>
                          {ON_CAMPUS_TIERS[setupData.licenseTier].pricePerSemester ? (
                            <>
                              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
                                ${ON_CAMPUS_TIERS[setupData.licenseTier].pricePerSemester}/semester
                              </div>
                              <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                                ${ON_CAMPUS_TIERS[setupData.licenseTier].pricePerYear}/year
                              </div>
                            </>
                          ) : (
                            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
                              Contact for custom pricing
                            </div>
                          )}
                        </div>
                        {setupData.licenseTier === '200_plus' && (
                          <div style={{ 
                            padding: '12px', 
                            background: '#fef3c7', 
                            borderRadius: '8px', 
                            color: '#92400e',
                            marginTop: '16px'
                          }}>
                            <strong>Note:</strong> For 200+ members, please contact us for custom pricing. We will set up your billing manually.
                          </div>
                        )}
                      </>
                    ) : (
                      <p>Calculating tier based on {memberCount} members...</p>
                    )}
                  </div>
                  <div className="form-actions">
                    <button
                      onClick={() => {
                        if (setupData.licenseTier === '200_plus') {
                          alert('For 200+ members, please contact us for custom pricing. We will set up your billing manually.');
                          return;
                        }
                        setSetupStep(2);
                      }}
                      className="btn-primary"
                      disabled={!setupData.licenseTier || setupData.licenseTier === '200_plus'}
                    >
                      Continue to Billing Information
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {setupStep === 1 && orgType !== 'on_campus_org' && (
            <div className="setup-step">
              <h3>Select License Tier</h3>
              <div className="tier-grid">
                {Object.entries(LICENSE_TIERS).map(([key, tier]) => (
                  <div
                    key={key}
                    role="button"
                    tabIndex={0}
                    className={`tier-card ${
                      setupData.licenseTier === key ? "selected" : ""
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleTierSelect(key);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleTierSelect(key);
                      }
                    }}
                  >
                    <h4>{tier.name}</h4>
                    {tier.price ? (
                      <div className="tier-price">
                        <div>${(tier.price * 6).toLocaleString()}/semester</div>
                        <div className="tier-price-yearly">${(tier.price * 12).toLocaleString()}/year</div>
                      </div>
                    ) : (
                      <div className="tier-price">Contact for pricing</div>
                    )}
                  </div>
                ))}
              </div>

              <div className="billing-interval-choice" style={{ marginTop: '24px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 600 }}>Billing frequency</label>
                <label className="interval-option" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                  <input
                    type="radio"
                    name="billingInterval"
                    value="semester"
                    checked={setupData.billingInterval === 'semester'}
                    onChange={() => setSetupData({ ...setupData, billingInterval: 'semester' })}
                  />
                  <span>Per semester (billed twice per year)</span>
                  {setupData.licenseTier && LICENSE_TIERS[setupData.licenseTier]?.price ? (
                    <strong>${(LICENSE_TIERS[setupData.licenseTier].price * 6).toLocaleString()}/semester</strong>
                  ) : (
                    <strong style={{ color: '#888' }}>Select a tier</strong>
                  )}
                </label>
                <label className="interval-option" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="radio"
                    name="billingInterval"
                    value="yearly"
                    checked={setupData.billingInterval === 'yearly'}
                    onChange={() => setSetupData({ ...setupData, billingInterval: 'yearly' })}
                  />
                  <span>Yearly (billed once per year, 2× semester rate)</span>
                  {setupData.licenseTier && LICENSE_TIERS[setupData.licenseTier]?.price ? (
                    <strong>${(LICENSE_TIERS[setupData.licenseTier].price * 12).toLocaleString()}/year</strong>
                  ) : (
                    <strong style={{ color: '#888' }}>Select a tier</strong>
                  )}
                </label>
              </div>

              <div className="form-actions" style={{ marginTop: '20px' }}>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => setSetupStep(2)}
                  disabled={!setupData.licenseTier || setupData.licenseTier === '20000_plus'}
                >
                  Continue to Billing Information
                </button>
              </div>
            </div>
          )}

          {setupStep === 2 && (
            <div className="setup-step">
              <h3>Billing Information</h3>
              <form onSubmit={handleBillingInfoSubmit}>
                <div className="form-group">
                  <label>
                    Billing Contact Email <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    value={setupData.billingContactEmail}
                    onChange={(e) =>
                      setSetupData({
                        ...setupData,
                        billingContactEmail: e.target.value,
                      })
                    }
                    required
                    placeholder="billing@example.edu"
                  />
                </div>

                <div className="form-group">
                  <label>PO Number (Optional)</label>
                  <input
                    type="text"
                    value={setupData.poNumber}
                    onChange={(e) =>
                      setSetupData({ ...setupData, poNumber: e.target.value })
                    }
                    placeholder="PO-12345"
                  />
                </div>

                <div className="form-group">
                  <label style={{ marginBottom: '8px', display: 'block' }}>Payment Information</label>
                  <div style={{ 
                    padding: '12px', 
                    background: '#f0fdfa', 
                    borderRadius: '8px', 
                    color: '#229aa5',
                    fontSize: '14px'
                  }}>
                    <strong>Payment Links:</strong> After setup, you'll receive a branded payment link via email each month. 
                    Payment links support ACH, credit cards, and other payment methods. You can also apply promo codes directly in the payment link.
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    onClick={() => setSetupStep(1)}
                    className="btn-secondary"
                  >
                    Back
                  </button>
                  <button type="submit" className="btn-primary">
                    Continue
                  </button>
                </div>
              </form>
            </div>
          )}

          {setupStep === 3 && (
            <div className="setup-step">
              <h3>Term Commitment</h3>
              <form onSubmit={handleTermCommitmentSubmit}>
                <div className="form-group">
                  <label>
                    Semester <span className="required">*</span>
                  </label>
                  <select
                    value={setupData.termSemester}
                    onChange={(e) =>
                      setSetupData({
                        ...setupData,
                        termSemester: e.target.value,
                      })
                    }
                    required
                  >
                    <option value="">Select semester...</option>
                    <option value="fall">Fall</option>
                    <option value="spring">Spring</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>
                    Year <span className="required">*</span>
                  </label>
                  <select
                    value={setupData.termYear}
                    onChange={(e) =>
                      setSetupData({
                        ...setupData,
                        termYear: parseInt(e.target.value),
                      })
                    }
                    required
                  >
                    {Array.from({ length: 10 }, (_, i) => {
                      const year = new Date().getFullYear() + i;
                      return (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="term-info">
                  <p>
                    Your commitment will run through{" "}
                    {setupData.termSemester === "fall"
                      ? `December 31, ${setupData.termYear}`
                      : setupData.termSemester === "spring"
                      ? `June 1, ${setupData.termYear}`
                      : "the end of the selected term"}
                    .
                  </p>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    onClick={() => setSetupStep(2)}
                    className="btn-secondary"
                  >
                    Back
                  </button>
                  <button type="submit" className="btn-primary">
                    Continue
                  </button>
                </div>
              </form>
            </div>
          )}

          {setupStep === 4 && (
            <div className="setup-step">
              <h3>Terms of Service Agreement</h3>
              <div className="terms-container">
                <div className="terms-content">
                  <h4>
                    SyllySync – Institutional Subscription Terms of Service
                  </h4>
                  <p>
                    <strong>Effective Date:</strong>{" "}
                    {new Date().toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                  <p>
                    <strong>Provider:</strong> SyllySync, LLC ("SyllySync,"
                    "we," "us")
                  </p>
                  <p>
                    <strong>Customer:</strong> The subscribing educational
                    institution ("Institution," "you")
                  </p>

                  <p>
                    These Institutional Subscription Terms of Service
                    ("Agreement") govern the Institution's access to and use of
                    the SyllySync platform for enrolled students.
                  </p>

                  <h5>1. Services Provided</h5>
                  <p>
                    SyllySync provides a cloud-based academic planning and study
                    support platform designed to assist students with syllabus
                    organization, academic planning, and study engagement
                    ("Services").
                  </p>
                  <p>
                    The Services are provided to the Institution's enrolled
                    students and authorized staff for educational purposes only.
                  </p>

                  <h5>2. Eligibility and Authorized Users</h5>
                  <p>
                    <strong>2.1 Institutional Eligibility</strong>
                  </p>
                  <p>
                    Only accredited colleges, universities, or educational
                    institutions may enter into this Agreement.
                  </p>
                  <p>
                    <strong>2.2 Authorized Users</strong>
                  </p>
                  <p>Authorized Users include:</p>
                  <ul>
                    <li>Currently enrolled students of the Institution</li>
                    <li>
                      Designated administrators and staff approved by the
                      Institution
                    </li>
                  </ul>
                  <p>
                    Access is restricted to users who authenticate using the
                    Institution's approved email domain(s) (e.g., .edu) or other
                    approved authentication methods.
                  </p>
                  <p>
                    The Institution is responsible for ensuring only eligible
                    users access the Services.
                  </p>

                  <h5>3. Subscription Term</h5>
                  <p>
                    <strong>3.1 Term Length</strong>
                  </p>
                  <p>
                    Subscriptions are billed on a monthly basis and are subject
                    to a minimum academic term commitment as defined in the
                    Order Form or invoice (e.g., Fall and Spring semesters).
                  </p>
                  <p>
                    <strong>3.2 Renewal</strong>
                  </p>
                  <p>
                    Subscriptions automatically renew monthly unless cancelled
                    in accordance with Section 8.
                  </p>

                  <h5>4. Fees and Billing</h5>
                  <p>
                    <strong>4.1 Pricing</strong>
                  </p>
                  <p>
                    Fees are based on the Institution's enrollment tier as
                    agreed upon in writing ("Subscription Fees").
                  </p>
                  <p>
                    <strong>4.2 Billing Method</strong>
                  </p>
                  <p>
                    SyllySync will invoice the Institution monthly via Stripe or
                    an agreed-upon invoicing system.
                  </p>
                  <p>
                    Invoices are payable within Net 30 days unless otherwise
                    stated.
                  </p>
                  <p>
                    <strong>4.3 Payment Method</strong>
                  </p>
                  <p>Payments may be made via:</p>
                  <ul>
                    <li>ACH transfer</li>
                    <li>Credit card</li>
                    <li>Other mutually agreed payment methods</li>
                  </ul>
                  <p>
                    <strong>4.4 Late Payments</strong>
                  </p>
                  <p>
                    SyllySync reserves the right to suspend access if payment is
                    more than 30 days past due, after providing written notice.
                  </p>

                  <h5>5. Changes in Enrollment</h5>
                  <p>Pricing tiers may be adjusted:</p>
                  <ul>
                    <li>At the start of a new academic term</li>
                    <li>Upon written agreement by both parties</li>
                  </ul>
                  <p>
                    SyllySync will not retroactively charge for minor enrollment
                    fluctuations within a term.
                  </p>

                  <h5>6. Data Privacy and FERPA</h5>
                  <p>
                    <strong>6.1 Student Data</strong>
                  </p>
                  <p>
                    SyllySync acts as a school official with a legitimate
                    educational interest as defined under FERPA.
                  </p>
                  <p>SyllySync will:</p>
                  <ul>
                    <li>Use student data solely to provide the Services</li>
                    <li>Not sell student data</li>
                    <li>Maintain commercially reasonable safeguards</li>
                  </ul>
                  <p>
                    <strong>6.2 Data Ownership</strong>
                  </p>
                  <p>
                    The Institution retains ownership of student data. SyllySync
                    retains ownership of the platform and related intellectual
                    property.
                  </p>

                  <h5>7. Acceptable Use</h5>
                  <p>The Institution agrees not to:</p>
                  <ul>
                    <li>
                      Use the Services for non-educational or commercial
                      purposes
                    </li>
                    <li>Reverse engineer or attempt to extract source code</li>
                    <li>Allow access by unauthorized individuals</li>
                  </ul>
                  <p>
                    SyllySync reserves the right to suspend access for misuse
                    after reasonable notice.
                  </p>

                  <h5>8. Cancellation and Termination</h5>
                  <p>
                    <strong>8.1 Cancellation by Institution</strong>
                  </p>
                  <p>
                    The Institution may cancel the subscription by providing 30
                    days written notice.
                  </p>
                  <p>
                    Cancellation becomes effective at the end of the current
                    academic term or minimum commitment period.
                  </p>
                  <p>
                    <strong>8.2 Cancellation by SyllySync</strong>
                  </p>
                  <p>SyllySync may terminate this Agreement:</p>
                  <ul>
                    <li>For material breach after 30 days' notice</li>
                    <li>Immediately if required by law</li>
                  </ul>

                  <h5>9. No Refunds for Partial Terms</h5>
                  <p>
                    Subscription Fees are non-refundable once a billing period
                    has begun, except where required by law.
                  </p>

                  <h5>10. Service Availability</h5>
                  <p>
                    SyllySync will use commercially reasonable efforts to
                    maintain platform availability but does not guarantee
                    uninterrupted access.
                  </p>
                  <p>
                    Planned maintenance will be communicated in advance when
                    feasible.
                  </p>

                  <h5>11. Limitation of Liability</h5>
                  <p>To the maximum extent permitted by law:</p>
                  <ul>
                    <li>
                      SyllySync's total liability shall not exceed the fees paid
                      by the Institution in the preceding 12 months.
                    </li>
                    <li>
                      SyllySync is not liable for indirect, incidental, or
                      consequential damages.
                    </li>
                  </ul>

                  <h5>12. Indemnification</h5>
                  <p>
                    Each party agrees to indemnify the other against claims
                    arising from its own negligence or misuse of the Services.
                  </p>

                  <h5>13. Governing Law</h5>
                  <p>
                    This Agreement is governed by the laws of the Commonwealth
                    of Pennsylvania, without regard to conflict of law
                    principles.
                  </p>

                  <h5>14. Amendments</h5>
                  <p>
                    SyllySync may update these Terms with reasonable notice.
                    Continued use after notice constitutes acceptance.
                  </p>

                  <h5>15. Entire Agreement</h5>
                  <p>
                    This Agreement, together with any Order Form or invoice,
                    constitutes the entire agreement between the parties.
                  </p>

                  <h5>16. Contact Information</h5>
                  <p>
                    SyllySync, LLC
                    <br />
                    420 E 3rd Ave, Derry, PA 15627
                    <br />
                    nate@syllysync.com
                  </p>
                </div>
              </div>

              <div className="terms-acceptance">
                <label
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "12px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={setupData.termsAccepted}
                    onChange={(e) =>
                      setSetupData({
                        ...setupData,
                        termsAccepted: e.target.checked,
                      })
                    }
                    style={{ marginTop: "4px", cursor: "pointer" }}
                  />
                  <span>
                    I have read and agree to the SyllySync Institutional
                    Subscription Terms of Service{" "}
                    <span className="required">*</span>
                  </span>
                </label>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setSetupStep(3)}
                  className="btn-secondary"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleTermsAccept}
                  className="btn-primary"
                  disabled={processing || !setupData.termsAccepted}
                >
                  {processing ? "Setting up..." : "Complete Setup"}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="billing-content">
          {billingInfo && (
            <div className="billing-info-section">
              <h2>Billing Information</h2>
              <div className="info-grid">
                <div className="info-item">
                  <label>License Tier</label>
                  <div>
                    {billingInfo.licenseTier
                      ? LICENSE_TIERS[billingInfo.licenseTier]?.name ||
                        billingInfo.licenseTier
                      : "Not set"}
                  </div>
                </div>
                <div className="info-item">
                  <label>Billing Contact</label>
                  <div>{billingInfo.billingContactEmail || "Not set"}</div>
                </div>
                <div className="info-item">
                  <label>PO Number</label>
                  <div>{billingInfo.poNumber || "Not set"}</div>
                </div>
                <div className="info-item">
                  <label>Payment Method</label>
                  <div>
                    {(() => {
                      // Calculate next payment date
                      const getNextPaymentDate = () => {
                        if (!billingInfo.termSemester || !billingInfo.termYear) {
                          return "Not set";
                        }

                        const now = new Date();
                        const termSemester = billingInfo.termSemester;
                        const termYear = billingInfo.termYear;
                        const isYearly = billingInfo.billingInterval === "yearly";

                        // Yearly: payment link sent December 15 for the following year. Semester: Aug 1 (Fall), Dec 15 (Spring).
                        let nextPaymentDate;
                        let nextPaymentSemester;
                        let nextPaymentYear;

                        if (isYearly) {
                          // Yearly: next payment link is always December 15 (for the following year's coverage)
                          const dec15ThisYear = new Date(now.getFullYear(), 11, 15); // Dec 15
                          if (dec15ThisYear > now) {
                            nextPaymentDate = dec15ThisYear;
                            nextPaymentYear = now.getFullYear() + 1; // link is for next year
                          } else {
                            nextPaymentDate = new Date(now.getFullYear() + 1, 11, 15);
                            nextPaymentYear = now.getFullYear() + 2;
                          }
                        } else {
                          if (termSemester === "fall") {
                            nextPaymentDate = new Date(termYear, 11, 15); // December 15
                            nextPaymentSemester = "Spring";
                            nextPaymentYear = termYear + 1;
                          } else if (termSemester === "spring") {
                            nextPaymentDate = new Date(termYear, 7, 1); // August 1
                            nextPaymentSemester = "Fall";
                            nextPaymentYear = termYear;
                          } else {
                            return "Not set";
                          }
                          if (nextPaymentDate < now) {
                            if (termSemester === "fall") {
                              nextPaymentDate = new Date(termYear + 1, 7, 1);
                              nextPaymentSemester = "Fall";
                              nextPaymentYear = termYear + 1;
                            } else {
                              nextPaymentDate = new Date(termYear, 11, 15);
                              nextPaymentSemester = "Spring";
                              nextPaymentYear = termYear + 1;
                            }
                          }
                        }

                        const formattedDate = nextPaymentDate.toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric'
                        });

                        return `Payment Link (Next: ${formattedDate}${isYearly ? ` for ${nextPaymentYear} annual coverage` : ` for ${nextPaymentSemester} ${nextPaymentYear}`})`;
                      };

                      return getNextPaymentDate();
                    })()}
                  </div>
                </div>
                <div className="info-item">
                  <label>Term Commitment</label>
                  <div>
                    {billingInfo.termSemester && billingInfo.termYear
                      ? `${
                          billingInfo.termSemester.charAt(0).toUpperCase() +
                          billingInfo.termSemester.slice(1)
                        } ${billingInfo.termYear}`
                      : "Not set"}
                  </div>
                </div>
                {orgType === 'university' && billingInfo.billingInterval && (
                  <div className="info-item">
                    <label>Billing frequency</label>
                    <div>
                      {billingInfo.billingInterval === 'yearly'
                        ? 'Yearly — payment link sent December 15 for the following year'
                        : 'Per semester — payment links sent August 1 (Fall) and December 15 (Spring)'}
                    </div>
                  </div>
                )}
                <div className="info-item">
                  <label>Term End Date</label>
                  <div>{formatDate(billingInfo.termEndDate)}</div>
                </div>
              </div>
            </div>
          )}

          <div className="invoices-section">
            <h2>Invoices</h2>
            {invoices.length === 0 ? (
              <div className="empty-state">
                <p>No invoices found.</p>
              </div>
            ) : (
              <div className="invoices-list">
                {invoices.map((invoice) => (
                  <div key={invoice.id} className="invoice-card">
                    <div className="invoice-header">
                      <div>
                        <h3>Invoice #{invoice.number}</h3>
                        <p className="invoice-date">
                          Created: {formatDate(invoice.created)}
                        </p>
                      </div>
                      <div className={`invoice-status ${invoice.status}`}>
                        {invoice.status === "paid"
                          ? "✓ Paid"
                          : invoice.status === "open"
                          ? "Open"
                          : invoice.status}
                      </div>
                    </div>
                    <div className="invoice-details">
                      <div className="invoice-amount">
                        <label>Amount Due:</label>
                        <span>
                          {formatCurrency(invoice.amountDue, invoice.currency)}
                        </span>
                      </div>
                      {invoice.dueDate && (
                        <div className="invoice-due-date">
                          <label>Due Date:</label>
                          <span>{formatDate(invoice.dueDate)}</span>
                        </div>
                      )}
                    </div>
                    <div className="invoice-actions">
                      {invoice.invoicePdf && (
                        <a
                          href={invoice.invoicePdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-download"
                        >
                          📄 Download PDF
                        </a>
                      )}
                      {invoice.hostedInvoiceUrl && (
                        <a
                          href={invoice.hostedInvoiceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-view"
                        >
                          View Invoice
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Billing;
